/* ============================================================================
 * useAppearance — the skin + knobs controller
 * ----------------------------------------------------------------------------
 * A single source for how the OS looks. It owns:
 *   • the base skin          → `data-skin` on <html>
 *   • the personal knobs     → `data-opt-*` on <html>
 *
 * Both are applied as attributes so the stylesheet does the work and no
 * component needs to know the current appearance. `canon` on a knob means
 * "unset the attribute", which lets the base skin's own value win — that is
 * what makes a personal setup portable across base skins.
 * ========================================================================= */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CANON,
  OPT_ATTR_KEYS,
  STORAGE_KEY,
  coerceAppearance,
  countOverrides,
  parseAppearance,
  serialize,
  type Appearance,
  type KnobKey,
} from '../lib/appearance';
import {
  SKINS,
  type Skin,
} from './useSkin';
import { applyForgedSkin } from '../lib/skinforge';

export interface AppearanceController {
  appearance: Appearance;
  /** Base skin — kept as `skin` so the menu bar's contract is unchanged. */
  skin: Skin;
  setSkin: (s: Skin) => void;
  cycle: () => void;
  set: <K extends KnobKey>(key: K, value: Appearance[K]) => void;
  reset: () => void;
  /** Knobs away from canon, for the "personalised" badge. */
  overrides: number;
  exportJson: () => string;
  importJson: (json: string) => { ok: boolean; error?: string };
}

function readInitial(): Appearance {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? coerceAppearance(JSON.parse(raw) as unknown) : { ...CANON };
  } catch {
    return { ...CANON };
  }
}

function applyToDocument(a: Appearance): void {
  const root = document.documentElement;
  root.setAttribute('data-skin', a.base);

  // Clear every knob attribute, then set only the ones that are not canon.
  for (const attr of OPT_ATTR_KEYS) root.removeAttribute(attr);
  for (const knob of OPT_ATTR_KEYS) {
    const key = knob.replace('data-opt-', '') as Lowercase<KnobKey>;
    const value = (a as unknown as Record<string, string>)[key];
    if (value && value !== 'canon') root.setAttribute(knob, value);
  }
}

export function useAppearanceState(): AppearanceController {
  const [appearance, setAppearance] = useState<Appearance>(readInitial);
  // Tracks the base skin so a change can clear any forged overlay.
  const prevBase = useRef(appearance.base);

  useEffect(() => {
    applyToDocument(appearance);
    // Choosing a built-in skin clears a forged one. A forged skin writes INLINE
    // custom properties, which outrank the stylesheet — so without this, the
    // forged palette would keep winning after the base skin changed.
    if (prevBase.current !== appearance.base) {
      prevBase.current = appearance.base;
      applyForgedSkin(null);
    }
    try {
      localStorage.setItem(STORAGE_KEY, serialize(appearance));
    } catch {
      /* non-fatal: applies for this session only */
    }
  }, [appearance]);

  const setSkin = useCallback(
    (s: Skin) => setAppearance((a) => ({ ...a, base: s })),
    [],
  );

  const cycle = useCallback(
    () =>
      setAppearance((a) => {
        const i = SKINS.indexOf(a.base);
        return { ...a, base: SKINS[(i + 1) % SKINS.length] };
      }),
    [],
  );

  const set = useCallback(
    <K extends KnobKey>(key: K, value: Appearance[K]) =>
      setAppearance((a) => ({ ...a, [key]: value })),
    [],
  );

  const reset = useCallback(
    () => setAppearance((a) => ({ ...CANON, base: a.base })),
    [],
  );

  const exportJson = useCallback(() => serialize(appearance), [appearance]);

  const importJson = useCallback((json: string) => {
    try {
      const next = parseAppearance(json);
      setAppearance(next);
      return { ok: true as const };
    } catch (e) {
      return {
        ok: false as const,
        error: e instanceof Error ? e.message : String(e),
      };
    }
  }, []);

  const overrides = useMemo(() => countOverrides(appearance), [appearance]);

  return {
    appearance,
    skin: appearance.base,
    setSkin,
    cycle,
    set,
    reset,
    overrides,
    exportJson,
    importJson,
  };
}
